import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './AddSupplementForm.scss';
import { Supplement, getAllSupplements } from '../../../api/supplements';
import { updateSupplementRegime } from '../../../api/supplementRegimes';
import Modal from '../../BaseComponents/Modal';
import TextInput from '../../BaseComponents/TextInput';
import TextArea from '../../BaseComponents/TextArea';
import Select from '../../BaseComponents/Select';

interface AddSupplementFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: SupplementFormData) => void;
  isSubmitting?: boolean;
  editingSupplement?: any | null;
  memberId: string;
}

interface SupplementFormData {
  member_id: string;
  supplement_id: number;
  dosage: number | string;
  dosage_units: string;
  meal_relation: string;
  time_of_day: string[];
  instructions_for_taking: string;
  additional_notes: string;
  _isUpdate?: boolean;
  _updatedId?: number;
}

const AddSupplementForm: React.FC<AddSupplementFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  editingSupplement,
  memberId
}) => {
  const [formData, setFormData] = useState<SupplementFormData>({
    member_id: memberId,
    supplement_id: 0,
    dosage: '',
    dosage_units: '',
    meal_relation: '',
    time_of_day: [],
    instructions_for_taking: '',
    additional_notes: ''
  });

  const [errors, setErrors] = useState<Partial<SupplementFormData>>({});
  const [supplementOptions, setSupplementOptions] = useState<Supplement[]>([]);
  const [isLoadingSupplements, setIsLoadingSupplements] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSupplements = async () => {
      try {
        setIsLoadingSupplements(true);
        // Fetch all supplements without any limit
        const response = await getAllSupplements();
        setSupplementOptions(response.supplements || []);
      } catch (error) {
        setSupplementOptions([]);
      } finally {
        setIsLoadingSupplements(false);
      }
    };

    fetchSupplements();
  }, []);
  useEffect(() => {
    const handleSupplementCreated = () => {
      const fetchSupplements = async () => {
        try {
          setIsLoadingSupplements(true);
          // Fetch all supplements without any limit
          const response = await getAllSupplements();
          setSupplementOptions(response.supplements || []);
        } catch (error) {
          setSupplementOptions([]);
        } finally {
          setIsLoadingSupplements(false);
        }
      };
      fetchSupplements();
    };

    window.addEventListener('supplement-created', handleSupplementCreated);
    
    return () => {
      window.removeEventListener('supplement-created', handleSupplementCreated);
    };
  }, []);

  useEffect(() => {
    if (editingSupplement) {
      let supplementId = editingSupplement.supplement_id || editingSupplement.id || 0;
      if (!supplementId && editingSupplement.supplement_name && supplementOptions.length > 0) {
        const foundSupplement = supplementOptions.find(supp => 
          supp.supplement_name === editingSupplement.supplement_name
        );
        if (foundSupplement) {
          supplementId = foundSupplement.id || foundSupplement.supplement_id || 0;
        }
      }
      
      const newFormData = {
        member_id: memberId,
        supplement_id: supplementId,
        dosage: editingSupplement.dosage || '',
        dosage_units: editingSupplement.dosage_units || editingSupplement.dosage_unit || '',
        meal_relation: editingSupplement.meal_relation || '',
        time_of_day: editingSupplement.time_of_day || [],
        instructions_for_taking: editingSupplement.instructions_for_taking || '',
        additional_notes: editingSupplement.additional_notes || ''
      };
      
      setFormData(newFormData);
    } else {
      const newFormData = {
        member_id: memberId,
        supplement_id: 0,
        dosage: '',
        dosage_units: '',
        meal_relation: '',
        time_of_day: [],
        instructions_for_taking: '',
        additional_notes: ''
      };
      
      setFormData(newFormData);
    }
  }, [editingSupplement, memberId, supplementOptions]);

  useEffect(() => {
    if (editingSupplement && supplementOptions.length > 0) {
      let supplementId = editingSupplement.supplement_id || editingSupplement.id || 0;
      if (!supplementId && editingSupplement.supplement_name) {
        const foundSupplement = supplementOptions.find(supp => 
          supp.supplement_name === editingSupplement.supplement_name
        );
        if (foundSupplement) {
          supplementId = foundSupplement.id || foundSupplement.supplement_id || 0;
        }
      }
      
      if (supplementId !== formData.supplement_id) {
        setFormData(prev => ({
          ...prev,
          supplement_id: supplementId
        }));
      }
    }
  }, [supplementOptions, editingSupplement, formData.supplement_id]);

  const mealRelationOptions = [
    { key: 'Before breakfast', value: 'Before breakfast' },
    { key: 'With breakfast', value: 'With breakfast' },
    { key: 'After breakfast', value: 'After breakfast' },
    { key: 'Before lunch', value: 'Before lunch' },
    { key: 'With lunch', value: 'With lunch' },
    { key: 'After lunch', value: 'After lunch' },
    { key: 'Before dinner', value: 'Before dinner' },
    { key: 'With dinner', value: 'With dinner' },
    { key: 'After dinner', value: 'After dinner' },
    { key: 'Before bed', value: 'Before bed' },
    { key: 'Anytime', value: 'Anytime' }
  ];

  const timeOfDayOptions = [
    'morning',
    'afternoon',
    'evening',
    'night',
    'day'
  ];

  const dosageUnits = [
    { key: 'mg', value: 'mg' },
    { key: 'µg', value: 'µg' },
    { key: 'g', value: 'g' },
    { key: 'mg_elemental', value: 'mg elemental' },
    { key: 'IU', value: 'IU' },
    { key: 'CFU', value: 'CFU' },
    { key: 'FU', value: 'FU' },
    { key: 'USP_units', value: 'USP units' },
    { key: '%', value: '%' },
    { key: 'ratio_extract', value: 'ratio extract' },
    { key: 'ppm', value: 'ppm' },
    { key: 'ml', value: 'ml' },
    { key: 'drops', value: 'drops' },
    { key: 'tsp', value: 'tsp' },
    { key: 'tbsp', value: 'tbsp' },
    { key: 'kcal', value: 'kcal' },
    { key: 'mcg', value: 'mcg' },
    {key:'Per_day',value:'Per day'}
  ];

  const supplementSelectOptions = supplementOptions.map(option => ({
    key: ((option.id || option.supplement_id) || 0).toString(),
    value: option.supplement_name || 'Unknown Supplement'
  }));

  const handleInputChange = (field: keyof SupplementFormData, value: string | number | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNumberInputChange = (field: 'supplement_id' | 'dosage', value: string) => {
    if (field === 'dosage') {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      const numValue = value === '' ? 0 : (parseInt(value) || 0);
      setFormData(prev => ({
        ...prev,
        [field]: numValue
      }));
      
      if (numValue > 0) {
        const selectedSupplement = supplementOptions.find(supp => 
          (supp.id || supp.supplement_id) === numValue
        );
        
        if (selectedSupplement) {
          const updates: Partial<SupplementFormData> = {};
          
          if (selectedSupplement.recommended_dosage !== undefined && selectedSupplement.recommended_dosage !== null) {
            updates.dosage = selectedSupplement.recommended_dosage.toString();
          }
          
          if (selectedSupplement.dosage_units !== undefined && selectedSupplement.dosage_units !== null) {
            const supplementDosageUnits = selectedSupplement.dosage_units.trim();
            if (supplementDosageUnits) {
              updates.dosage_units = supplementDosageUnits;
            }
          }
          
          if (selectedSupplement.instructions_for_taking) {
            updates.instructions_for_taking = selectedSupplement.instructions_for_taking;
          }
          
          if (Object.keys(updates).length > 0) {
            setFormData(prev => ({
              ...prev,
              ...updates
            }));
          }
        }
      } else {
        setFormData(prev => ({
          ...prev,
          dosage: '',
          dosage_units: '',
          instructions_for_taking: ''
        }));
      }
    }
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTimeOfDayChange = (time: string) => {
    setFormData(prev => ({
      ...prev,
      time_of_day: prev.time_of_day.includes(time)
        ? prev.time_of_day.filter(t => t !== time)
        : [...prev.time_of_day, time]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.supplement_id || Number(formData.supplement_id) <= 0) {
      newErrors.supplement_id = 'Supplement is required';
    }
    if (!formData.dosage || (typeof formData.dosage === 'number' && formData.dosage <= 0) || (typeof formData.dosage === 'string' && (formData.dosage.trim() === '' || Number(formData.dosage) <= 0))) {
      newErrors.dosage = 'Dosage is required and must be greater than 0';
    }
    if (!formData.instructions_for_taking.trim()) {
      newErrors.instructions_for_taking = 'Instructions are required';
    }
    if (formData.instructions_for_taking.length > 200) {
      newErrors.instructions_for_taking = 'Instructions must be 200 characters or less';
    }
    if (!formData.meal_relation.trim()) {
      newErrors.meal_relation = 'Meal relation is required';
    }
    if (formData.time_of_day.length === 0) {
      newErrors.time_of_day = 'Time of day is required';
    }

    if (!formData.member_id || formData.member_id.trim() === '') {
      newErrors.member_id = 'Member ID is required';
    }
    if (!formData.dosage_units || formData.dosage_units.trim() === '') {
      newErrors.dosage_units = 'Dosage units are required';
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      try {
        const formattedData = {
          member_id: formData.member_id,
          supplement_id: Number(formData.supplement_id),
          dosage: Number(formData.dosage),
          dosage_units: formData.dosage_units,
          meal_relation: formData.meal_relation,
          time_of_day: formData.time_of_day,
          instructions_for_taking: formData.instructions_for_taking,
          additional_notes: formData.additional_notes
        };

        if (!formattedData.member_id || typeof formattedData.member_id !== 'string') {
          throw new Error('Invalid member_id format');
        }
        if (!formattedData.supplement_id || typeof formattedData.supplement_id !== 'number' || formattedData.supplement_id <= 0) {
          throw new Error('Invalid supplement_id format');
        }
        if (!formattedData.dosage || typeof formattedData.dosage !== 'number' || formattedData.dosage <= 0) {
          throw new Error('Invalid dosage format');
        }
        if (!formattedData.meal_relation || typeof formattedData.meal_relation !== 'string') {
          throw new Error('Invalid meal_relation format');
        }
        if (!Array.isArray(formattedData.time_of_day) || formattedData.time_of_day.length === 0) {
          throw new Error('Invalid time_of_day format');
        }
        if (!formattedData.instructions_for_taking || typeof formattedData.instructions_for_taking !== 'string') {
          throw new Error('Invalid instructions_for_taking format');
        }
        if (!formattedData.dosage_units || typeof formattedData.dosage_units !== 'string' || formattedData.dosage_units.trim() === '') {
          throw new Error('Invalid dosage_units format');
        }
        
        if (editingSupplement) {
          try {
            const updatePayload = {
              regime_id: editingSupplement.id,
              member_id: formattedData.member_id,
              supplement_id: formattedData.supplement_id,
              dosage: formattedData.dosage,
              dosage_units: formattedData.dosage_units,
              meal_relation: formattedData.meal_relation,
              time_of_day: formattedData.time_of_day,
              instructions_for_taking: formattedData.instructions_for_taking,
              additional_notes: formattedData.additional_notes
            };
            
            await updateSupplementRegime(updatePayload);
            
            const finalData = { 
              ...formattedData, 
              _isUpdate: true, 
              _updatedId: editingSupplement.id 
            };
            
            await onSubmit(finalData);
            toast.success('Supplement regime updated successfully!');
          } catch (updateError) {
            throw updateError;
          }
        } else {
          await onSubmit(formattedData);
          toast.success('Supplement regime added successfully!');
        }
        
        setFormData({
          member_id: memberId,
          supplement_id: 0,
          dosage: 0,
          dosage_units: '',
          meal_relation: '',
          time_of_day: [],
          instructions_for_taking: '',
          additional_notes: ''
        });
      } catch (error) {
        toast.error('Failed to save supplement. Please try again.');
      }
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={editingSupplement ? 'Edit Supplement Regime' : 'Add a Supplement Regime'}
      width="800px"
    >
      <form className="supplement-form" onSubmit={handleSubmit}>
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-section">
          <h3>Supplement Selection</h3>
          <div className="form-row">
            <Select
              name="supplement"
              options={supplementSelectOptions}
              selectedOption={{ key: formData.supplement_id.toString(), value: supplementOptions.find(s => (s.id || s.supplement_id) === formData.supplement_id)?.supplement_name || 'Select a supplement' }}
              onChange={(option: { key: string; value: string }) => handleNumberInputChange('supplement_id', option.key)}
              placeholder="Select a supplement"
              isError={!!errors.supplement_id}
            />
            {errors.supplement_id && <span className="error-message">{errors.supplement_id}</span>}
            {supplementOptions.length === 0 && !isLoadingSupplements && (
              <span className="error-message">No supplements available</span>)}
          </div>
        </div>

        <div className="form-section">
          <h3>Dosage Information</h3>
          <div className="form-row two-columns">
            <TextInput
              name="dosage"
              placeholder="Enter dosage"
              value={typeof formData.dosage === 'string' ? formData.dosage : formData.dosage.toString()}
              onChange={(e) => handleNumberInputChange('dosage', e.target.value)}
              isError={!!errors.dosage}
              errorMessage={typeof errors.dosage === 'string' ? errors.dosage : undefined}
            />
            <Select
              name="dosage_units"
              options={dosageUnits}
              selectedOption={formData.dosage_units ? { key: formData.dosage_units, value: formData.dosage_units } : { key: '', value: '' }}
              onChange={(option: { key: string; value: string }) => handleInputChange('dosage_units', option.key)}
              placeholder="Select units"
              isError={!!errors.dosage_units}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Administration Details</h3>
          <div className="form-row">
            <Select
              name="meal_relation"
              options={mealRelationOptions}
              selectedOption={{ key: formData.meal_relation, value: formData.meal_relation }}
              onChange={(option: { key: string; value: string }) => handleInputChange('meal_relation', option.key)}
              placeholder="Select meal relation"
              isError={!!errors.meal_relation}
            />
            {errors.meal_relation && <span className="error-message">{errors.meal_relation}</span>}
          </div>

          <div className="form-row">
            <div className="time-of-day-options">
              {timeOfDayOptions.map(time => (
                <label key={time} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.time_of_day.includes(time)}
                    onChange={() => handleTimeOfDayChange(time)}
                  />
                  <span className="checkbox-text">{time.charAt(0).toUpperCase() + time.slice(1)}</span>
                </label>
              ))}
            </div>
            {errors.time_of_day && <span className="error-message">{errors.time_of_day}</span>}
          </div>
        </div>

        <div className="form-section">
          <h3>Instructions for Taking</h3>
          <div className="form-row">
            <TextArea
              name="instructions_for_taking"
              placeholder="Take with breakfast and a glass of water."
              value={formData.instructions_for_taking}
              onChange={(e) => handleInputChange('instructions_for_taking', e.target.value)}
              isError={!!errors.instructions_for_taking}
              errorMessage={errors.instructions_for_taking || undefined}
            />
            <div className="char-limit">Max 200 characters - when/how to take</div>
          </div>
        </div>

        <div className="form-section">
          <h3>Additional Notes (optional)</h3>
          <div className="form-row">
            <TextArea
              name="additional_notes"
              placeholder="Enter any additional notes or interactions"
              value={formData.additional_notes}
              onChange={(e) => handleInputChange('additional_notes', e.target.value)}
              isError={!!errors.additional_notes}
              errorMessage={errors.additional_notes || undefined}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : editingSupplement ? 'Update Supplement Regime' : 'Add a Supplement Regime'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddSupplementForm;
